'use client'

import { useAtomValue } from 'jotai'

import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { parseExchangeField } from '@/lib/proto/util'
import { orderFormSelectionAtomFamily } from '@/state/order'
import { exchangeAtom } from '@/state/storage'
import { Exchange, type TradeVenue } from '@/types/generated/common/types'

/**
 * The Order Form's venue — taken from its OWN ticket (the first row's exchange), falling back
 * to the active account's venue while the ticket is empty.
 *
 * Never from the linking group's focused tab (useCardAccountContext): the Order Form is
 * independent of other widgets' tabs and account pickers. Switching a chart tab, or the
 * chart's account, must not re-point the form's venue, default account or rows — rows only
 * enter the form through explicit adds, each carrying its own exchange and account.
 */
export function useOrderFormTradeVenue(cardId: string | undefined): TradeVenue | undefined {
  const { selectedInstruments, selectedOptions } = useAtomValue(
    orderFormSelectionAtomFamily(cardId ?? '')
  )
  const activeAccount = useAtomValue(exchangeAtom)

  const firstOption = selectedOptions[0]
  const rowExchange =
    selectedInstruments[0]?.exchange ??
    firstOption?.exchange ??
    (firstOption?.ticker ? String(firstOption.ticker.exchange) : undefined)
  if (rowExchange !== undefined && rowExchange !== '') {
    const venue = exchangeToTradeVenue(
      parseExchangeField(rowExchange, Exchange.EXCHANGE_UNSPECIFIED)
    )
    if (venue !== undefined) return venue
  }
  return activeAccount?.tradeVenue
}
